import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactElement, ReactNode } from 'react'
import type { InstagramTrackStatus } from './InstagramTrackDetails'

export type InstagramMusicPlaybackControls = {
  toggle: () => void
  pause: () => void
}

type PlaybackContext = {
  audition: ({ videoKey: string; trackId: string } & AuditionOptions) | null
  previewVideo: (
    videoKey: string,
    trackId: string,
    options?: AuditionOptions,
  ) => () => void
  statuses: Record<string, InstagramTrackStatus>
  register: (
    key: string,
    controls: InstagramMusicPlaybackControls,
  ) => () => void
  report: (key: string, status: InstagramTrackStatus) => void
  toggle: (key: string) => void
  stop: (key: string) => void
}

type AuditionOptions = {
  audio?: HTMLAudioElement
  videoVolume?: number
  onEnded?: () => void
  onError?: () => void
}

const Context = createContext<PlaybackContext | null>(null)
export const useInstagramMusicPlayback = (): PlaybackContext | null =>
  useContext(Context)

export function InstagramMusicPlaybackProvider({
  children,
  onOpenPreview,
}: {
  children: ReactNode
  onOpenPreview?: () => void
}): ReactElement {
  const [audition, setAudition] = useState<PlaybackContext['audition']>(null)
  const onOpenPreviewRef = useRef(onOpenPreview)
  onOpenPreviewRef.current = onOpenPreview
  const previewVideo = useCallback(
    (
      videoKey: string,
      trackId: string,
      options?: AuditionOptions,
    ): (() => void) => {
      const next = { videoKey, trackId, ...options }
      setAudition(next)
      onOpenPreviewRef.current?.()
      return () => setAudition((current) => (current === next ? null : current))
    },
    [],
  )
  const controls = useRef(new Map<string, InstagramMusicPlaybackControls>())
  const pending = useRef<string | null>(null)
  const [statuses, setStatuses] = useState<
    Record<string, InstagramTrackStatus>
  >({})
  const report = useCallback(
    (key: string, status: InstagramTrackStatus): void => {
      if (
        (status === 'unavailable' || status === 'error') &&
        pending.current === key
      )
        pending.current = null
      setStatuses((current) =>
        current[key] === status ? current : { ...current, [key]: status },
      )
    },
    [],
  )
  const register = useCallback(
    (key: string, player: InstagramMusicPlaybackControls): (() => void) => {
      controls.current.set(key, player)
      if (pending.current === key) {
        pending.current = null
        player.toggle()
      }
      return (): void => {
        if (controls.current.get(key) !== player) return
        controls.current.delete(key)
        player.pause()
        report(key, 'idle')
      }
    },
    [report],
  )
  const toggle = useCallback(
    (key: string): void => {
      onOpenPreview?.()
      const player = controls.current.get(key)
      if (player) player.toggle()
      else {
        pending.current = pending.current === key ? null : key
        report(key, pending.current ? 'loading' : 'idle')
      }
    },
    [report, onOpenPreview],
  )
  const stop = useCallback(
    (key: string): void => {
      if (pending.current === key) pending.current = null
      controls.current.get(key)?.pause()
      report(key, 'idle')
    },
    [report],
  )
  const value = useMemo(
    () => ({
      statuses,
      report,
      register,
      toggle,
      stop,
      audition,
      previewVideo,
    }),
    [statuses, report, register, toggle, stop, audition, previewVideo],
  )
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function instagramPlaybackKey(
  channelId: string,
  audioId: string,
  videoUrl: string,
): string {
  return JSON.stringify([channelId, audioId, videoUrl])
}
